<script setup lang="ts">
import UIBanner from '@/shared/ui/Banner';
import UIButton from '@/shared/ui/Button/index';
import Filters from '@/pages/catalog-detail/ui/Filters/index.vue';
import IconSortDown from '@/shared/ui/Icons/SortDown.vue';
import ProductCardList from '@/widgets/productCardList/index';
import UIBreadcrumbs from '@/shared/ui/Breadcrumbs/index';
import Pagination from '@/widgets/pagination/index';

import { useCatalogDetail } from '@/pages/catalog-detail/composables/useCatalogDetail';
import { useSort } from '@/pages/catalog-detail/composables/useSort';
import { useFilterVisibility } from '@/pages/catalog-detail/composables/useFilterVisibility';

const {
  currentPage,
  totalPages,
  catalogProducts,
  title,
  breadcrumbs,
  isShowPagination,
  changeCurrentPage,
} = await useCatalogDetail();

const { isHightPrice, isActiveSort, toggleSort, resetSort, sortButtonText } = useSort();
const { isShowFilters, filtersToggleText, toggleFilters } = useFilterVisibility(true);

const { t } = useI18n();
</script>

<template>
  <div :class="['catalog-detail', { _hide: !isShowFilters }]">
    <div class="catalog-detail__breadcrumbs">
      <UIBreadcrumbs :breadcrumbs="breadcrumbs" />
    </div>
    <div class="catalog-detail__button-wrapper _toggle">
      <UIButton
        :text="filtersToggleText"
        gray
        :aria-label="filtersToggleText"
        @click="toggleFilters"
      />
    </div>
    <div class="catalog-detail__content">
      <aside v-show="isShowFilters" class="catalog-detail__sidebar">
        <Filters />
      </aside>
      <div class="catalog-detail__main">
        <div class="catalog-detail__banner">
          <UIBanner
            :title="t('dailyServing') + ' <br /> ' + t('beautyAndHealth')"
            :description="t('bestQuality') + ' <br /> ' + t('bestQualityDescription')"
            :images="{
              desktop: '/images/banners/catalog/catalog-banner.png',
              mobile: '/images/banners/catalog/catalog-banner-mobile.png',
            }"
          />
        </div>
        <div class="catalog-detail__products">
          <div class="catalog-detail__products-header">
            <h1 class="catalog-detail__title">{{ title }}</h1>
            <div class="catalog-detail__buttons">
              <div class="catalog-detail__button-wrapper _default">
                <UIButton
                  :text="t('default')"
                  light-orange
                  :aria-label="t('default')"
                  @click="resetSort"
                />
              </div>
              <div class="catalog-detail__button-wrapper _sort">
                <UIButton
                  :text="sortButtonText"
                  :gray="!isActiveSort"
                  :light-orange="isActiveSort"
                  :aria-label="sortButtonText"
                  :class="['catalog-detail__button', { _active: isActiveSort }]"
                  @click="toggleSort"
                >
                  <template #icon-right>
                    <IconSortDown
                      :class="['catalog-detail__icon', { _low: !isHightPrice }]"
                    />
                  </template>
                </UIButton>
              </div>
            </div>
          </div>
          <div class="catalog-detail__products-list">
            <ProductCardList
              :products="catalogProducts"
              gap="24px"
              :column-width="!isShowFilters ? '23%' : '31%'"
            />
          </div>
        </div>
        <div v-if="isShowPagination" class="catalog-detail__pagination">
          <Pagination
            v-model="currentPage"
            :key="currentPage"
            :total-pages="totalPages"
            show-load-more
            :load-more-text="t('moreProducts')"
            @on-change="
              (page: number | string) =>
                changeCurrentPage(Number(page) as number, 'change')
            "
            @on-load-more="
              (page: number | string) =>
                changeCurrentPage(Number(page) as number, 'load-more')
            "
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="less">
.catalog-detail {
  .container-d();
  .flex-column(@size-x6);

  margin-top: @size-x8;
  margin-bottom: 56px;

  &__content {
    display: flex;
    gap: @size-x5;
  }

  &__main {
    .flex-column(@size-x8);

    width: 100%;
    max-width: 1060px;
    margin-top: -67px;
  }

  &__sidebar {
    width: 100%;
    max-width: 340px;
  }

  &__products {
    .flex-column(@size-x6);

    width: 100%;
  }

  &._hide &__main {
    max-width: 100%;
    margin-top: 0;
  }

  &__button-wrapper {
    &._toggle {
      width: 183px;
    }

    &._default {
      width: 142px;
    }

    &._sort {
      width: 192px;
    }
  }

  &__banner {
    height: 250px;
  }

  &__products-header {
    display: flex;
    justify-content: space-between;
    gap: @size-x8;
  }

  &__title {
    .h2();
  }

  &__buttons {
    display: flex;
    gap: @size-x4;
  }

  &__button {
    &:hover .catalog-detail__icon path {
      transition: fill @A linear;
      fill: @color-tango;
    }

    &._active .catalog-detail__icon path {
      fill: @color-tango;
    }
  }

  &__icon {
    &._low {
      transform: rotate(180deg);
    }
  }
}
</style>
